import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { format, parseISO } from "date-fns";
import {
  Receipt,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Ban,
  Flag,
  FlagOff,
  Search,
  X,
  TrendingUp,
  CreditCard,
  Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

interface LineItem {
  id: string;
  description: string;
  amountCents: number;
  quantity: number;
  period: { start: string | null; end: string | null } | null;
}

interface Invoice {
  id: string;
  type: "subscription" | "credit_purchase";
  status: string;
  needsAttention: boolean;
  flaggedHandled: boolean;
  flagNote: string | null;
  amountDueCents: number;
  amountPaidCents: number;
  amountRemainingCents: number;
  currency: string;
  stripeCustomerId: string;
  workspaceName: string | null;
  customerId: string | null;
  subscriptionId: string | null;
  issuedAt: string | null;
  paidAt: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
  attemptCount: number;
  nextPaymentAttempt: string | null;
  lineItems: LineItem[];
}

interface InvoicesResponse {
  invoices: Invoice[];
  stripeConnected: boolean;
  needsAttentionCount: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMoney(cents: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return format(parseISO(iso), "MMM d, yyyy");
}

function fmtPeriod(start: string | null, end: string | null) {
  if (!start && !end) return null;
  if (start && end) return `${fmtDate(start)} – ${fmtDate(end)}`;
  return fmtDate(start ?? end);
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status, needsAttention }: { status: string; needsAttention: boolean }) {
  const map: Record<string, { label: string; cls: string }> = {
    paid: { label: "Paid", cls: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" },
    void: { label: "Void", cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400" },
    uncollectible: { label: "Uncollectible", cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
    open: { label: needsAttention ? "Open — Attention" : "Open", cls: needsAttention ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400" : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
    draft: { label: "Draft", cls: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400" },
  };
  const cfg = map[status] ?? { label: status, cls: "bg-gray-100 text-gray-600" };
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium", cfg.cls)}>
      {needsAttention && <AlertTriangle className="h-3 w-3 mr-1" />}
      {cfg.label}
    </span>
  );
}

function TypeBadge({ type }: { type: "subscription" | "credit_purchase" }) {
  return type === "subscription" ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
      <TrendingUp className="h-3 w-3" /> Subscription
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
      <CreditCard className="h-3 w-3" /> Credits
    </span>
  );
}

// ─── Line items sub-row ────────────────────────────────────────────────────────

function LineItemsPane({ invoice }: { invoice: Invoice }) {
  if (!invoice.lineItems.length) {
    return <p className="text-xs text-muted-foreground px-4 py-3">No line items available.</p>;
  }
  return (
    <div className="border-t bg-gray-50/60 dark:bg-gray-800/40 px-4 py-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
        Line Items
      </div>
      <div className="space-y-1">
        {invoice.lineItems.map((li) => {
          const period = fmtPeriod(li.period?.start ?? null, li.period?.end ?? null);
          return (
            <div key={li.id} className="flex items-start justify-between gap-4 text-sm">
              <div className="flex-1 min-w-0">
                <span className="text-gray-800 dark:text-gray-200">{li.description || "—"}</span>
                {period && (
                  <span className="ml-2 text-xs text-muted-foreground">{period}</span>
                )}
              </div>
              <div className="shrink-0 text-right">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {fmtMoney(li.amountCents, invoice.currency)}
                </span>
                {li.quantity > 1 && (
                  <span className="ml-1 text-xs text-muted-foreground">×{li.quantity}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Total row */}
      <div className="border-t mt-2 pt-2 flex items-center justify-between text-sm font-semibold text-gray-900 dark:text-gray-100">
        <span>Total due</span>
        <span>{fmtMoney(invoice.amountDueCents, invoice.currency)}</span>
      </div>
      {invoice.amountPaidCents > 0 && invoice.amountPaidCents !== invoice.amountDueCents && (
        <div className="flex items-center justify-between text-xs text-muted-foreground mt-0.5">
          <span>Paid</span>
          <span>{fmtMoney(invoice.amountPaidCents, invoice.currency)}</span>
        </div>
      )}
    </div>
  );
}

// ─── Flag dialog ──────────────────────────────────────────────────────────────

function FlagDialog({
  invoice,
  open,
  onClose,
}: {
  invoice: Invoice;
  open: boolean;
  onClose: () => void;
}) {
  const [note, setNote] = useState(invoice.flagNote ?? "");
  const qc = useQueryClient();
  const { toast } = useToast();

  const flagMutation = useMutation({
    mutationFn: (body: { remove: boolean; note: string }) =>
      apiRequest("POST", `/api/platform/invoices/${invoice.id}/flag`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["/api/platform/invoices"] });
      toast({
        title: vars.remove ? "Flag removed" : "Marked as handled",
        description: vars.remove
          ? "Invoice will reappear in Needs Attention."
          : "Invoice removed from Needs Attention list.",
      });
      onClose();
    },
    onError: () => toast({ title: "Error", description: "Could not update flag.", variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark as Handled</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-sm text-muted-foreground">
            This removes the invoice from the <strong>Needs Attention</strong> list without retrying the
            charge. Use it when you've contacted the customer directly.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="flag-note" className="text-sm">Note (optional)</Label>
            <Textarea
              id="flag-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Customer updating card — follow up May 30"
              rows={3}
              maxLength={500}
              data-testid="textarea-flag-note"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {invoice.flaggedHandled && (
            <Button
              variant="outline"
              onClick={() => flagMutation.mutate({ remove: true, note: "" })}
              disabled={flagMutation.isPending}
              data-testid="button-remove-flag"
            >
              <FlagOff className="h-4 w-4 mr-1.5" /> Remove flag
            </Button>
          )}
          <Button
            onClick={() => flagMutation.mutate({ remove: false, note })}
            disabled={flagMutation.isPending}
            data-testid="button-confirm-flag"
          >
            {flagMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Flag className="h-4 w-4 mr-1.5" />}
            Mark handled
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Invoice row ─────────────────────────────────────────────────────────────

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  const [expanded, setExpanded] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const qc = useQueryClient();
  const { toast } = useToast();

  const retryMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/platform/invoices/${invoice.id}/retry`, {}),
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["/api/platform/invoices"] });
      toast({ title: "Retry successful", description: `Invoice status: ${data.status}` });
    },
    onError: (err: any) =>
      toast({ title: "Retry failed", description: err.message ?? "Stripe returned an error.", variant: "destructive" }),
  });

  const voidMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/platform/invoices/${invoice.id}/void`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/platform/invoices"] });
      toast({ title: "Invoice voided" });
    },
    onError: (err: any) =>
      toast({ title: "Void failed", description: err.message ?? "Stripe returned an error.", variant: "destructive" }),
  });

  const canRetry = invoice.status === "open" || invoice.status === "uncollectible";
  const canVoid = invoice.status === "open" || invoice.status === "draft";
  const canFlag = invoice.status !== "paid" && invoice.status !== "void";

  const ChevronIcon = expanded ? ChevronDown : ChevronRight;

  return (
    <>
      <div
        className={cn(
          "grid grid-cols-[20px_1fr_140px_110px_110px_90px_140px] items-center gap-3 px-4 py-3 border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors text-sm",
          invoice.needsAttention && !invoice.flaggedHandled && "bg-amber-50/40 dark:bg-amber-900/10"
        )}
        data-testid={`invoice-row-${invoice.id}`}
      >
        {/* Expand chevron */}
        <button
          onClick={() => setExpanded((p) => !p)}
          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          data-testid={`button-expand-${invoice.id}`}
        >
          <ChevronIcon className="h-4 w-4" />
        </button>

        {/* Customer + date */}
        <div className="min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {invoice.workspaceName ?? invoice.stripeCustomerId}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
            <span>{fmtDate(invoice.issuedAt)}</span>
            {invoice.flaggedHandled && (
              <span className="inline-flex items-center gap-0.5 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-3 w-3" /> Handled
              </span>
            )}
          </div>
        </div>

        {/* Type */}
        <div><TypeBadge type={invoice.type} /></div>

        {/* Amount */}
        <div className="text-right font-medium tabular-nums text-gray-900 dark:text-gray-100">
          {fmtMoney(invoice.amountDueCents, invoice.currency)}
        </div>

        {/* Paid */}
        <div className="text-right tabular-nums text-muted-foreground">
          {invoice.amountPaidCents > 0 ? fmtMoney(invoice.amountPaidCents, invoice.currency) : "—"}
        </div>

        {/* Status */}
        <div>
          <StatusBadge status={invoice.status} needsAttention={invoice.needsAttention && !invoice.flaggedHandled} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 justify-end">
          {canRetry && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending || voidMutation.isPending}
              title="Retry charge"
              data-testid={`button-retry-${invoice.id}`}
            >
              {retryMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
          {canVoid && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:border-red-300"
              onClick={() => voidMutation.mutate()}
              disabled={retryMutation.isPending || voidMutation.isPending}
              title="Void invoice"
              data-testid={`button-void-${invoice.id}`}
            >
              {voidMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Ban className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
          {canFlag && (
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "h-7 px-2 text-xs",
                invoice.flaggedHandled && "border-green-300 text-green-700"
              )}
              onClick={() => setFlagOpen(true)}
              title={invoice.flaggedHandled ? "Handled (click to update)" : "Mark as handled"}
              data-testid={`button-flag-${invoice.id}`}
            >
              {invoice.flaggedHandled ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <Flag className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
          {invoice.hostedInvoiceUrl && (
            <a
              href={invoice.hostedInvoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in Stripe"
              data-testid={`link-stripe-${invoice.id}`}
            >
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground">
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Expanded line items */}
      {expanded && <LineItemsPane invoice={invoice} />}

      {/* Flag dialog */}
      {flagOpen && (
        <FlagDialog invoice={invoice} open={flagOpen} onClose={() => setFlagOpen(false)} />
      )}
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PlatformInvoices() {
  const [tab, setTab] = useState<"all" | "subscription" | "credits" | "attention">("all");
  const [search, setSearch] = useState("");

  const { data, isLoading, isError, refetch, isFetching } = useQuery<InvoicesResponse>({
    queryKey: ["/api/platform/invoices"],
  });

  const invoices = data?.invoices ?? [];
  const stripeConnected = data?.stripeConnected ?? true;
  const needsAttentionCount = data?.needsAttentionCount ?? 0;

  const filtered = useMemo(() => {
    let result = invoices;

    if (tab === "subscription") result = result.filter((i) => i.type === "subscription");
    else if (tab === "credits") result = result.filter((i) => i.type === "credit_purchase");
    else if (tab === "attention") result = result.filter((i) => i.needsAttention);

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (i) =>
          (i.workspaceName ?? "").toLowerCase().includes(q) ||
          i.id.toLowerCase().includes(q) ||
          i.stripeCustomerId.toLowerCase().includes(q) ||
          i.status.toLowerCase().includes(q)
      );
    }

    return result;
  }, [invoices, tab, search]);

  // Summary stats
  const totalRevenueCents = invoices
    .filter((i) => i.status === "paid")
    .reduce((s, i) => s + i.amountPaidCents, 0);
  const outstandingCents = invoices
    .filter((i) => i.status === "open")
    .reduce((s, i) => s + i.amountRemainingCents, 0);

  return (
    <PlatformLayout>
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Receipt className="h-6 w-6 text-gray-400" />
            Invoices Hub
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            All Stripe charges across every customer workspace — subscriptions and credit purchases.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="button-refresh-invoices"
        >
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {/* Not-connected notice */}
      {!isLoading && !stripeConnected && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Stripe not connected</p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              Connect Stripe in the Replit integrations panel to view live invoices.
            </p>
          </div>
        </div>
      )}

      {/* Summary cards */}
      {!isLoading && stripeConnected && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border bg-white dark:bg-gray-900 p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center shrink-0">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                {fmtMoney(totalRevenueCents, "USD")}
              </div>
              <div className="text-xs text-muted-foreground">Total collected</div>
            </div>
          </div>
          <div className="rounded-lg border bg-white dark:bg-gray-900 p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
              <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                {fmtMoney(outstandingCents, "USD")}
              </div>
              <div className="text-xs text-muted-foreground">Outstanding (open)</div>
            </div>
          </div>
          <div
            className={cn(
              "rounded-lg border p-4 flex items-center gap-3",
              needsAttentionCount > 0
                ? "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-700"
                : "bg-white dark:bg-gray-900"
            )}
          >
            <div
              className={cn(
                "h-9 w-9 rounded-lg flex items-center justify-center shrink-0",
                needsAttentionCount > 0
                  ? "bg-amber-100 dark:bg-amber-900/40"
                  : "bg-gray-50 dark:bg-gray-800"
              )}
            >
              <AlertTriangle
                className={cn(
                  "h-5 w-5",
                  needsAttentionCount > 0
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-gray-400"
                )}
              />
            </div>
            <div>
              <div
                className={cn(
                  "text-lg font-semibold tabular-nums",
                  needsAttentionCount > 0
                    ? "text-amber-800 dark:text-amber-300"
                    : "text-gray-900 dark:text-gray-100"
                )}
              >
                {needsAttentionCount}
              </div>
              <div className="text-xs text-muted-foreground">Needs attention</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
          <TabsList className="h-8">
            <TabsTrigger value="all" className="text-xs px-3" data-testid="tab-all-invoices">
              All
            </TabsTrigger>
            <TabsTrigger value="subscription" className="text-xs px-3" data-testid="tab-subscription-invoices">
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="credits" className="text-xs px-3" data-testid="tab-credits-invoices">
              Credits
            </TabsTrigger>
            <TabsTrigger
              value="attention"
              className="text-xs px-3 relative"
              data-testid="tab-attention-invoices"
            >
              Needs Attention
              {needsAttentionCount > 0 && (
                <span className="ml-1.5 inline-flex h-4 min-w-[1rem] px-1 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold text-black">
                  {needsAttentionCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, ID…"
            className="pl-8 h-8 text-sm"
            data-testid="input-invoice-search"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gray-700"
              data-testid="button-clear-search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="ml-auto text-xs text-muted-foreground">
          {filtered.length} invoice{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-white dark:bg-gray-900 overflow-hidden">
        {/* Header row */}
        <div className="grid grid-cols-[20px_1fr_140px_110px_110px_90px_140px] gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border-b text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          <div />
          <div>Customer / Date</div>
          <div>Type</div>
          <div className="text-right">Amount Due</div>
          <div className="text-right">Paid</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading invoices from Stripe…</span>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-16 gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <span className="text-sm">Failed to load invoices.</span>
          </div>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Receipt className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm font-medium">
              {tab === "attention"
                ? "No invoices need attention — all clear."
                : "No invoices found."}
            </p>
            {search && (
              <button
                onClick={() => setSearch("")}
                className="mt-2 text-xs text-blue-600 hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        )}

        {!isLoading && !isError && filtered.map((inv) => (
          <InvoiceRow key={inv.id} invoice={inv} />
        ))}
      </div>

      {/* Legend */}
      {!isLoading && filtered.length > 0 && (
        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-4 pt-1">
          <span className="flex items-center gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Retry charge
          </span>
          <span className="flex items-center gap-1.5">
            <Ban className="h-3.5 w-3.5" /> Void invoice
          </span>
          <span className="flex items-center gap-1.5">
            <Flag className="h-3.5 w-3.5" /> Mark as handled (removes from Needs Attention without charging)
          </span>
          <span className="flex items-center gap-1.5">
            <ExternalLink className="h-3.5 w-3.5" /> Open in Stripe dashboard
          </span>
        </div>
      )}
    </div>
    </PlatformLayout>
  );
}
